// eslint-disable-next-line no-unused-vars
class GA4Datalayer {
	constructor(dataLayer) {
		this.dataLayer = dataLayer;
		this.useGlobalId = false;
		this.batchSize = 30;

		this.events = {
			viewItem: {
				// event: view_item | View PDP page
				selectors: ["#pdp"],
				func: this.pushViewItem.bind(this),
			},
			selectItem: {
				// event: select_item | Click on a product in a carousel list or PLPs
				selectors: ["[data-layer='select_item']"],
				func: GA4Datalayer.addEvents.bind(this, { action: "click", callback: this.pushSelectItem.bind(this) }),
			},
			selectVariant: {
				//event: select_variant | User has selected a product variant
				selectors: ["#variant-selector"],
				func: this.pushSelectVariant.bind(this, "#variant-selector"),
			},
			selectVariantUpdate: {
				// event: select_variant | User has changed a product variant
				selectors: ["#variant-options-form"],
				func: GA4Datalayer.addEvents.bind(this, {
					action: "change",
					callback: this.pushSelectVariant.bind(this, "#variant-selector"),
				}),
			},
			viewItemList: {
				// event: view_item_list | List of products in a page carousel list or PLPs
				selectors: ["[data-layer='view_item_list']"],
				func: this.pushItemList.bind(this),
			},
			viewCart: {
				// event: view_cart | open cart drawer
				selectors: ["#cart-drawer-toggle"],
				func: GA4Datalayer.addEvents.bind(this, {
					action: "click",
					callback: this.pushViewCart.bind(this),
				}),
			},
		};

		// Override default push() to always include experiments
		this.decoratePush();

		this.init();
	}

	decoratePush() {
		const originalPush = this.dataLayer.push.bind(this.dataLayer);

		this.dataLayer.push = (payload) => {
			if (payload && typeof payload === "object" && payload.event) {
				const experiments = this.getExperimentsFromCookie();

				if (Object.keys(experiments).length > 0) {
					// Object with all experiments
					payload.experiments = experiments;

					// String format(allows add only one dimension on GA4, but then more work on GA4 exploration)
					const expString = Object.keys(experiments)
						.map((key) => {
							const cleanKey = key.replace(/[^a-zA-Z0-9]/g, "_");
							return `${cleanKey}:${experiments[key]}`;
						})
						.join("|");

					payload.experiments_string = expString;

					// Flatten dynamically for GA4( need a dimension for each exp)
					Object.keys(experiments).forEach((key) => {
						const cleanKey = key.replace(/[^a-zA-Z0-9]/g, "_");
						payload[`exp_${cleanKey}`] = experiments[key];
					});
				}
			}
			// console.log("DATALAYER PUSH", payload);
			return originalPush(payload);
		};
	}

	getExperimentsFromCookie() {
		const match = document.cookie.match(/(^| )ab_exp=([^;]+)/);
		if (!match) return {};

		try {
			const parsed = JSON.parse(decodeURIComponent(match[2]));
			const result = {};

			Object.values(parsed).forEach((entry) => {
				const [exp, variant] = entry.split(":");
				if (exp && variant) {
					result[exp] = variant;
				}
			});

			return result;
		} catch (e) {
			return {};
		}
	}

	static addEvents(event, targets) {
		targets.forEach((target) => {
			target.addEventListener(event.action, event.callback);
		});
	}

	static arraySplit(items, chunkSize) {
		if (!chunkSize || chunkSize <= 0) return [items];
		const chunks = [];
		for (let i = 0; i < items.length; i += chunkSize) {
			chunks.push(items.slice(i, i + chunkSize));
		}
		return chunks;
	}

	formatItemId(globalId) {
		if (!globalId) return null;
		if (this.useGlobalId) return globalId;
		return globalId.replace(/\D+/g, "");
	}

	pushItemList(listNodes) {
		if (!Array.isArray(listNodes) || listNodes.length === 0) return;

		listNodes.forEach((list) => {
			const listItems = Array.from(list.querySelectorAll("article"));

			// Remove items like collections from productSliderRow
			const filteredItems = listItems.filter((item) => item.getAttribute("data-product"));

			// send in chunks
			GA4Datalayer.arraySplit(filteredItems, this.batchSize).forEach((items) => {
				const payload = [];
				items.forEach((item) => {
					const data = JSON.parse(item.getAttribute("data-product") || null);
					if (data) {
						payload.push({
							item_id: data.sku,
							item_name: data.title,
							item_variant: data.variant,
							item_category: data.category,
							shopify_product_id: this.formatItemId(data.product_id),
							shopify_variant_id: this.formatItemId(data.variantId),
							price: data.price,
							item_list_name: data.list,
							index: data.position,
						});
					}
				});

				this.dataLayer.push({ ecommerce: null });
				this.dataLayer.push({
					event: "view_item_list",
					ecommerce: {
						currency: "GBP",
						items: payload,
					},
				});
			});
		});
	}

	pushViewItem() {
		const pdpPage = document.getElementById("pdp");
		const variantSelector = document.querySelector("#variant-selector input");
		if (!pdpPage || !variantSelector) return;

		const productData = JSON.parse(pdpPage.getAttribute("data-product") || null);
		if (!productData) return;

		const { sku, price, title, variantAvailable } = variantSelector.dataset;

		this.dataLayer.push({ ecommerce: null });
		this.dataLayer.push({
			event: "view_item",
			ecommerce: {
				currency: "GBP",
				value: parseFloat(price),
				items: [
					{
						item_id: sku,
						item_name: productData.title,
						item_variant: title,
						item_category: productData.category,
						shopify_product_id: this.formatItemId(productData.product_id),
						shopify_variant_id: this.formatItemId(variantSelector.value),
						price: parseFloat(price),
						in_stock: !!variantAvailable,
						quantity: 1,
					},
				],
			},
		});
	}

	pushSelectItem({ currentTarget }) {
		if (!currentTarget) return;

		const data = JSON.parse(currentTarget.getAttribute("data-product") || null);
		if (!data) return;

		const imageUrl = currentTarget.querySelector("img").src;

		this.dataLayer.push({ ecommerce: null });
		this.dataLayer.push({
			event: "select_item",
			ecommerce: {
				currency: "GBP",
				value: data.price,
				items: [
					{
						item_id: data.sku,
						item_name: data.title,
						item_variant: data.variant,
						item_category: data.category,
						shopify_product_id: this.formatItemId(data.product_id),
						shopify_variant_id: this.formatItemId(data.variantId),
						price: parseFloat(data.price),
						item_list_name: data.list,
						index: data.position,
					},
				],
			},
		});
	}

	pushSelectVariant(variantFormId) {
		const variantForm = document.querySelector(variantFormId);
		const selectedOption = variantForm.querySelector("input:checked");

		if (!selectedOption) return;

		const product = document.querySelector("#pdp[data-product]");
		const productData = JSON.parse(product.getAttribute("data-product") || null);
		if (!productData) return;

		const variantAvailable = selectedOption.getAttribute("data-variant-available") === "true";

		if (variantAvailable) {
			this.dataLayer.push({ ecommerce: null });
			this.dataLayer.push({
				event: "select_variant",
				ecommerce: {
					currency: productData.currency,
					value: parseFloat(selectedOption.getAttribute("data-price")),
					items: [
						{
							item_id: selectedOption.getAttribute("data-sku"),
							item_name: productData.title,
							item_variant: selectedOption.getAttribute("data-title"),
							item_category: productData.category,
							shopify_product_id: this.formatItemId(productData.product_id),
							shopify_variant_id: this.formatItemId(selectedOption.value),
							price: parseFloat(selectedOption.getAttribute("data-price")),
							in_stock: variantAvailable,
							quantity: 1,
						},
					],
				},
			});
		}
	}

	pushViewCart() {
		const cartDrawer = document.getElementById("cart-drawer");
		if (!cartDrawer || cartDrawer.getAttribute("aria-expanded") !== "true") return;

		const items =
			window.cart?.cartObject?.lines?.nodes.map((line) => ({
				image: line.merchandise.image.url,
				item_id: line.merchandise.sku,
				item_name: line.merchandise.product.title,
				item_variant: line.merchandise.title,
				item_category: line.merchandise.product.productType,
				shopify_product_id: this.formatItemId(line.merchandise.product.id),
				shopify_variant_id: this.formatItemId(line.merchandise.id),
				price: parseFloat(line.merchandise.price.amount),
				in_stock: line.merchandise.availableForSale,
				shipping_required: line.merchandise.requiresShipping,
				quantity: line.quantity,
			})) || [];

		this.dataLayer.push({ ecommerce: null });
		this.dataLayer.push({
			event: "view_cart",
			ecommerce: {
				currency: "GBP",
				value: window.cart?.subtotal || 0,
				total_items: window.cart?.cartSize || 0,
				items,
			},
		});
	}

	pushItems(detail, eventName) {
		const { items: nodes, eventLabel } = detail;

		const payload = {
			event: eventName,
			...(eventLabel && { event_label: eventLabel }),
			ecommerce: {
				currency: "GBP",
				value: 0,
				items: [],
			},
		};

		nodes
			.filter((item) => Object.keys(item).length !== 0) // remove empty objects
			.forEach((node) => {
				const price = parseFloat(node.merchandise.price.amount);
				if (node.merchandise.id) {
					payload.ecommerce.items.push({
						image: node.merchandise.image.url,
						item_id: node.merchandise.sku,
						item_name: node.merchandise.product.title,
						item_variant: node.merchandise.title,
						item_category: node.merchandise.product.productType,
						shopify_product_id: this.formatItemId(node.merchandise.product.id),
						shopify_variant_id: this.formatItemId(node.merchandise.id),
						price: parseFloat(node.merchandise.price.amount),
						in_stock: node.merchandise.availableForSale,
						shipping_required: node.merchandise.requiresShipping,
						quantity: node.quantity,
					});
					payload.ecommerce.value += price * node.quantity;
				}
			});

		this.dataLayer.push({ ecommerce: null });
		this.dataLayer.push(payload);
	}

	init() {
		Object.values(this.events).forEach(({ selectors, func }) => {
			const listNodes = [];
			selectors.forEach((selector) => {
				listNodes.push(...(Array.from(document.querySelectorAll(selector)) || []));
			});

			if (listNodes && listNodes.length > 0) func(listNodes);
		});

		// Listen for cart events
		window.addEventListener("cart:addItems", ({ detail }) => {
			this.pushItems(detail, "add_to_cart");
		});
		window.addEventListener("cart:removeItems", ({ detail }) => {
			this.pushItems(detail, "remove_from_cart");
		});
	}
}

window.addEventListener("load", () => {
	if (window.dataLayer) {
		window.GA4DataLayer = new GA4Datalayer(window.dataLayer);
	} else {
		console.warn("GA4Datalayer tracker not loaded");
	}
});
